import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Rating,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import StarIcon from '@mui/icons-material/Star';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import EventIcon from '@mui/icons-material/Event';
import { formatDate } from '../../utils/helpers';
import { EventCategory, getCategoryLabel, getCategoryColor } from '../../types';
import api from '../../services/api';

interface FeedbackItem {
  id: string;
  eventId: string;
  userId: string;
  rating: number;
  enjoyedMost: string;
  improvements: string;
  additionalComments: string | null;
  wouldRecommend: boolean;
  submittedAt: string;
  userName: string;
  eventTitle: string;
  eventDate: string;
  eventCategory: string;
}

interface EventOption {
  id: string;
  title: string;
  date: string;
}

const FeedbackReportContent: React.FC = () => {
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [events, setEvents] = useState<EventOption[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  const fetchFeedback = useCallback(async () => {
    try {
      setLoading(true);
      const url = selectedEventId === 'all' ? '/feedback' : `/feedback?eventId=${selectedEventId}`;
      const response = await api.get(url);
      setFeedback(response.data);

      // Extract unique events from feedback for dropdown
      if (selectedEventId === 'all') {
        const uniqueEvents = new Map<string, EventOption>();
        response.data.forEach((f: FeedbackItem) => {
          if (!uniqueEvents.has(f.eventId)) {
            uniqueEvents.set(f.eventId, {
              id: f.eventId,
              title: f.eventTitle,
              date: f.eventDate,
            });
          }
        });
        setEvents(Array.from(uniqueEvents.values()));
      }
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  // Calculate statistics
  const totalFeedback = feedback.length;
  const averageRating =
    totalFeedback > 0
      ? feedback.reduce((sum, f) => sum + f.rating, 0) / totalFeedback
      : 0;
  const recommendationRate =
    totalFeedback > 0
      ? (feedback.filter((f) => f.wouldRecommend).length / totalFeedback) * 100
      : 0;

  // Rating distribution
  const ratingDistribution = [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: feedback.filter((f) => f.rating === rating).length,
    percentage:
      totalFeedback > 0
        ? (feedback.filter((f) => f.rating === rating).length / totalFeedback) * 100
        : 0,
  }));

  if (loading && feedback.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Filter */}
      <Box sx={{ mb: 4 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Filter by Event</InputLabel>
          <Select
            value={selectedEventId}
            label="Filter by Event"
            onChange={(e) => setSelectedEventId(e.target.value)}
          >
            <MenuItem value="all">All Events</MenuItem>
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.title} ({formatDate(event.date)})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {totalFeedback === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <FeedbackIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No feedback received yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Feedback will appear here once attendees submit their reviews after events.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <FeedbackIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />
                  <Typography variant="h3" fontWeight={700}>
                    {totalFeedback}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Responses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <StarIcon sx={{ fontSize: 40, color: '#ffc107', mb: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <Typography variant="h3" fontWeight={700}>
                      {averageRating.toFixed(1)}
                    </Typography>
                    <Typography variant="h5" color="text.secondary">
                      / 5
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Average Rating
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ThumbUpIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                  <Typography variant="h3" fontWeight={700}>
                    {recommendationRate.toFixed(0)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Would Recommend
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Rating Distribution */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Rating Distribution
              </Typography>
              <Box sx={{ mt: 2 }}>
                {ratingDistribution.map(({ rating, count, percentage }) => (
                  <Box
                    key={rating}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 80 }}>
                      <Rating value={rating} readOnly size="small" max={rating} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor:
                              rating >= 4 ? '#4caf50' : rating >= 3 ? '#ff9800' : '#f44336',
                          },
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'right' }}>
                      {count} ({percentage.toFixed(0)}%)
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Feedback Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Individual Feedback
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Event</TableCell>
                      <TableCell>Family</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>What They Enjoyed</TableCell>
                      <TableCell>Improvements</TableCell>
                      <TableCell>Recommend</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedback.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EventIcon
                              sx={{
                                color: getCategoryColor(item.eventCategory as EventCategory),
                                fontSize: 20,
                              }}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {item.eventTitle}
                              </Typography>
                              <Chip
                                label={getCategoryLabel(item.eventCategory as EventCategory)}
                                size="small"
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 18,
                                  backgroundColor: getCategoryColor(item.eventCategory as EventCategory),
                                  color: 'white',
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{item.userName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Rating value={item.rating} readOnly size="small" />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={item.enjoyedMost}>
                            {item.enjoyedMost}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={item.improvements}>
                            {item.improvements}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {item.wouldRecommend ? (
                            <Chip
                              icon={<ThumbUpIcon sx={{ fontSize: 14 }} />}
                              label="Yes"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip
                              icon={<ThumbDownIcon sx={{ fontSize: 14 }} />}
                              label="No"
                              size="small"
                              color="error"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(item.submittedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default FeedbackReportContent;
